import { useEffect, useState } from "react";
import FormFieldInput from "../FormField/FormFieldInput";
import OlMap, { ParcelInfo } from "../Map/OlMap";
import { Button } from "@components/ui/button";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import UldkSearch from "../UldkSearch/UldkSearch";
import { useGetTenantQuery } from "@/src/api/Tenant";
import useAuth from "@/src/hooks/useAuth";
import useUldk from "@/src/hooks/useUldk";
import Loader from "../Loader/Loader";
// GeoJSON handled by OlMap
import { Parcel } from "../ParcelPreview/ParcelPreview";
import { useParams } from "@tanstack/react-router";
import { Trash2, Plus } from "lucide-react";
import { DEFAULT_MAP_CENTER, type LatLng } from "@/src/lib/procedureMapView";
import { isRealTenantId } from "@/src/lib/tenantSlug";

type Place = {
  addresstype: string;
  boundingbox: string[];
  category: string;
  display_name: string;
  importance: number;
  lat: string;
  licence: string;
  lon: string;
  name: string;
  osm_id: number;
  osm_type: string;
  place_id: number;
  place_rank: number;
  type: "string";
};

export type { Center } from "../ParcelPreview/ParcelPreview";

async function fetchPlaceList(searchQuery: string) {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=jsonv2&countrycodes=pl&limit=8`,
    );
    if (!response.ok) throw new Error("Failed to fetch data");
    return (await response.json()) as Place[];
  } catch (e) {
    console.error("Error fetch: ", e);
  }
}

type SearchLocationProps = {
  addParcels?: (data: Parcel[]) => void;
  initialCenter?: LatLng;
  initialZoom?: number;
};

function SearchLocation({
  addParcels,
  initialCenter,
  initialZoom,
}: SearchLocationProps = {}) {
  const [placeList, setPlaceList] = useState<Place[]>([]);
  const [selectedParcels, setSelectedParcels] = useState<Parcel[]>([]);
  const [clickedParcelInfo, setClickedParcelInfo] = useState<ParcelInfo | null>(null);
  const [latLng, setLatLng] = useState(
    () => initialCenter ?? DEFAULT_MAP_CENTER,
  );
  const { auth } = useAuth();
  const { tenant } = useParams({ strict: false });
  const tenantId = isRealTenantId(auth.tenantId) ? auth.tenantId : tenant;
  const { data, isLoading } = useGetTenantQuery(tenantId || "");
  const { getParcelGeometryById } = useUldk({});

  const formSchema = z.object({ searchQuery: z.string({ required_error: "Podaj lokację" }) });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { searchQuery: "" },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    fetchPlaceList(values.searchQuery).then((list) => setPlaceList(list || []));
  }

  const handleParcelClick = async (info: ParcelInfo | null) => {
    setClickedParcelInfo(info);
    if (info?.id) {
      // Auto-add to selected if not already there
      if (!selectedParcels.some((p) => p.id === info.id)) {
        try {
          const geometry = await getParcelGeometryById(info.id);
          setSelectedParcels((prev) => [...prev, { id: info.id, geometry }]);
        } catch (err) { console.error("Failed to fetch parcel geometry:", err); }
      }
    }
  };

  const addParcelManually = (parcel: Parcel) => {
    if (!selectedParcels.some((p) => p.id === parcel.id)) {
      setSelectedParcels((prev) => [...prev, parcel]);
    }
  };

  const removeParcel = (id: string) => {
    setSelectedParcels((prev) => prev.filter((p) => p.id !== id));
  };


  // Fly to last selected parcel
  useEffect(() => {
    const last = selectedParcels[selectedParcels.length - 1];
    if (last) {
      const geo = Array.isArray(last.geometry) ? last.geometry[0] : last.geometry;
      if (geo?.center) setLatLng(geo.center);
    }
  }, [selectedParcels]);

  if (isLoading) return <Loader />;

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open && initialCenter) {
          setLatLng(initialCenter);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button><Plus className="h-4 w-4 mr-1" /> Dodaj działkę</Button>
      </DialogTrigger>
      <DialogContent className="max-w-[1225px]">
        <DialogHeader>
          <DialogTitle>Wyszukaj i wybierz działki</DialogTitle>
          <DialogDescription>Klikaj na mapie lub wyszukuj ręcznie. Wybrane działki podświetlą się na zielono.</DialogDescription>
        </DialogHeader>
        <div className="flex">
          <div className="min-w-[200px] w-1/2 px-3 overflow-y-auto max-h-[70vh]">
            {/* Nominatim search */}
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-x-4 flex items-end justify-stretch mb-3">
                <FormFieldInput name="searchQuery" form={form} description="" placeholder="np. Kraków, ul. Floriańska 1" label="Wyszukaj adres" />
                <Button type="submit" className="mb-1">Szukaj</Button>
              </form>
            </Form>
            {placeList.length > 0 && (
              <ul className="mb-4 border rounded-lg divide-y max-h-[120px] overflow-y-auto">
                {placeList.map(({ display_name, place_id, lat, lon }) => (
                  <li className="cursor-pointer text-xs p-2 hover:bg-blue-50" key={place_id} onClick={() => setLatLng({ lat: Number(lat), lng: Number(lon) })}>
                    {display_name}
                  </li>
                ))}
              </ul>
            )}

            {/* Parcel info from map click */}
            {clickedParcelInfo && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <h4 className="font-semibold text-sm mb-1 text-blue-800">Ostatnio kliknięta działka</h4>
                <div className="text-xs">
                  <span className="font-medium">{clickedParcelInfo.id}</span> — {clickedParcelInfo.commune}, {clickedParcelInfo.district}
                </div>
              </div>
            )}

            {/* Selected parcels list */}
            <div className="mb-3">
              <h4 className="font-semibold text-sm mb-2">
                Wybrane działki ({selectedParcels.length})
              </h4>
              {selectedParcels.length === 0 ? (
                <p className="text-xs text-gray-400">Kliknij na mapie aby wybrać działkę</p>
              ) : (
                <div className="space-y-1">
                  {selectedParcels.map((p) => (
                    <div key={p.id} className="flex items-center justify-between bg-green-50 border border-green-300 rounded px-3 py-1.5">
                      <span className="text-sm font-medium text-green-800">{p.id}</span>
                      <button onClick={() => removeParcel(p.id)} className="text-red-400 hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ULDK manual search */}
            <UldkSearch
              latLng={latLng}
              voivodeship={data?.payload?.voivodeship}
              district={data?.payload?.district}
              tenant={data?.payload?.tenant}
              setParcel={(parcel: Parcel) => {
                if (parcel) addParcelManually(parcel);
              }}
            />
          </div>
          <div className="min-h-[350px] h-[70vh] w-full">
            <OlMap
              latLng={latLng}
              setLatLng={setLatLng}
              initialZoom={initialZoom}
              onParcelClick={handleParcelClick}
              selectedParcelsGeometries={selectedParcels}
            />
          </div>
        </div>
        <div className="flex justify-between items-center pt-2 border-t">
          <span className="text-sm text-gray-500">
            {selectedParcels.length > 0
              ? `Wybrano ${selectedParcels.length} ${selectedParcels.length === 1 ? "działkę" : "działek"}`
              : "Nie wybrano żadnej działki"}
          </span>
          <DialogClose asChild>
            <Button
              onClick={() => {
                if (selectedParcels.length > 0) {
                  addParcels?.(selectedParcels);
                  setSelectedParcels([]);
                }
              }}
              disabled={selectedParcels.length === 0}
            >
              Dodaj {selectedParcels.length > 0 ? `${selectedParcels.length} ${selectedParcels.length === 1 ? "działkę" : "działek"}` : "działki"} do wniosku
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SearchLocation;
